import { useState } from 'react';
import type { RecursoDesfazivel } from '@triade/contracts';
import { toast } from 'sonner';
import { useDesfazer, useHistoricoRegistro } from '@/api/desfazer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatarDataHora } from '@/lib/format';

export interface HistoricoRegistroProps {
  recurso: RecursoDesfazivel;
  chave: string;
}

/** Botao "Histórico": abre a lista de alteracoes do registro (quem, quando, o que). So a linha `desfazivel` tem "Desfazer". */
export function HistoricoRegistro({ recurso, chave }: HistoricoRegistroProps) {
  const [aberto, setAberto] = useState(false);
  const historico = useHistoricoRegistro(recurso, chave, aberto);
  const desfazer = useDesfazer();

  function desfazerItem(id: string) {
    desfazer.mutate(id, {
      onSuccess: () => toast.success('Alteração desfeita'),
      onError: (e) => toast.error(e.message),
    });
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setAberto(true)}>
        Histórico
      </Button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Histórico de alterações</DialogTitle>
            <DialogDescription>Quem alterou e quando. Só a última alteração pode ser desfeita.</DialogDescription>
          </DialogHeader>

          {historico.isError ? (
            <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
              {historico.error.message}
            </p>
          ) : !historico.data ? (
            <p role="status" className="text-small text-ink-muted">
              Carregando histórico…
            </p>
          ) : historico.data.itens.length === 0 ? (
            <p role="status" className="text-small text-ink-muted">
              Sem alterações registradas.
            </p>
          ) : (
            <ul className="flex max-h-[60vh] flex-col divide-y divide-line-soft overflow-y-auto" aria-label="Alterações registradas">
              {historico.data.itens.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 py-2.5 text-small" data-testid="historico-registro-item">
                  <div className="min-w-0">
                    <div className="font-medium">{item.rotulo}</div>
                    <div className="text-caption text-ink-muted">
                      {item.usuarioNome ?? '—'} · {formatarDataHora(item.createdAt)}
                    </div>
                  </div>
                  {item.desfazivel && (
                    <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" disabled={desfazer.isPending} onClick={() => desfazerItem(item.id)}>
                      Desfazer
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
